import { ApplicationController } from "../../support/application-controller";

/**
 * Controller for individual manual review duplicate resolution forms.
 * Handles radio button selection UI, dry run toggle, and async preview.
 */
export default class extends ApplicationController {
    static targets = [
        "form",
        "card",
        "radio",
        "dryRunCheckbox",
        "submitButton",
        "submitButtonText",
        "resolutionPreview",
        "keepBadge",
        "keepBadgeText",
        "transferBadge",
        "transferBadgeText",
        "reasonDisplay",
        "errorMessage",
        "acceptContainer",
        "acceptButton",
        "acceptButtonText"
    ];

    connect() {
        this.externalSourceId = this.data.get("externalSourceId");
        this.resolveUrl = this.data.get("resolveUrl");
        this.wizardMode = this.data.get("wizardMode") === "true";
        this.wizardNextUrl = this.data.get("wizardNextUrl");
        this._updateSelectedCard();
        this._updateButtonState();
    }

    selectSubscription(event) {
        this._updateSelectedCard();
        // Reset preview when selection changes
        this._resetPreview();
    }

    toggleDryRun(event) {
        this._updateButtonState();
        // Reset preview when toggling dry run
        this._resetPreview();
    }

    /**
     * Accept the previewed resolution and execute it (non-dry-run)
     */
    acceptResolution(event) {
        event.preventDefault();

        const selectedRadio = this.radioTargets.find(radio => radio.checked);
        if (!selectedRadio) {
            this._showError("Please select a subscription to keep");
            return;
        }

        const targetSubscriptionId = selectedRadio.value;

        // Show loading state on Accept button
        this._setAcceptLoading(true);
        this._hideError();

        const self = this;

        fetch(this.resolveUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": this._getCSRFToken()
            },
            body: JSON.stringify({
                target_subscription_id: targetSubscriptionId
                // No dry_run flag = actual execution
            })
        })
        .then(function(response) {
            return response.json().then(function(data) {
                if (!response.ok || data.success === false) {
                    throw new Error(data.error || "Failed to execute resolution");
                }
                return data;
            });
        })
        .then(function(data) {
            // Resolution successful - navigate to next or reload
            if (self.wizardMode && self.wizardNextUrl) {
                // In wizard mode, navigate to the next duplicate
                window.location.href = self.wizardNextUrl;
            } else {
                // Normal mode - reload the page to show updated list
                window.location.reload();
            }
        })
        .catch(function(error) {
            console.error("Execute resolution error:", error);
            self._showError(error.message || "An error occurred while executing the resolution");
            self._setAcceptLoading(false);
        });
    }

    /**
     * Handle form submission - intercept for dry-run async preview
     */
    handleSubmit(event) {
        const isDryRun = this.hasDryRunCheckboxTarget && this.dryRunCheckboxTarget.checked;

        // Only intercept for dry-run mode; let actual execution go through normally
        if (!isDryRun) {
            return; // Let the form submit normally
        }

        event.preventDefault();

        const selectedRadio = this.radioTargets.find(radio => radio.checked);
        if (!selectedRadio) {
            this._showError("Please select a subscription to keep");
            return;
        }

        const targetSubscriptionId = selectedRadio.value;

        // Show loading state
        this._setLoading(true);
        this._hideError();

        const self = this;

        fetch(this.resolveUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": this._getCSRFToken()
            },
            body: JSON.stringify({
                target_subscription_id: targetSubscriptionId,
                dry_run: "true"
            })
        })
        .then(function(response) {
            return response.json().then(function(data) {
                if (!response.ok || data.success === false) {
                    throw new Error(data.error || "Failed to preview resolution");
                }
                return data;
            });
        })
        .then(function(data) {
            // Update UI with preview results
            self._showPreview(data);
            self._setLoading(false);
        })
        .catch(function(error) {
            console.error("Preview resolution error:", error);
            self._showError(error.message || "An error occurred while previewing the resolution");
            self._setLoading(false);
        });
    }

    /**
     * Show the resolution preview badges and update card labels
     */
    _showPreview(data) {
        const { target_subscription_id, transferred_from } = data;

        // Update the header badges
        if (this.hasResolutionPreviewTarget) {
            // Update keep badge text
            if (this.hasKeepBadgeTextTarget) {
                this.keepBadgeTextTarget.textContent = `Keep: Subscription #${target_subscription_id}`;
            }

            // Update transfer badge text and visibility
            if (this.hasTransferBadgeTarget && this.hasTransferBadgeTextTarget) {
                if (transferred_from && transferred_from.length > 0) {
                    const transferIds = transferred_from.map(id => `#${id}`).join(", ");
                    this.transferBadgeTextTarget.textContent = `Transfer from: ${transferIds}`;
                    this.transferBadgeTarget.classList.remove("duplicate-mappings__resolution-badge--hidden");
                } else {
                    this.transferBadgeTarget.classList.add("duplicate-mappings__resolution-badge--hidden");
                }
            }

            // Show the preview container
            this.resolutionPreviewTarget.classList.remove("duplicate-mappings__resolution-preview--hidden");
        }

        // Hide the reason display
        if (this.hasReasonDisplayTarget) {
            this.reasonDisplayTarget.classList.add("duplicate-mappings__reason--hidden");
        }

        // Update subscription card labels
        this._updateCardLabels(target_subscription_id, transferred_from || []);

        // Show the Accept button
        if (this.hasAcceptContainerTarget) {
            this.acceptContainerTarget.classList.remove("duplicate-mappings__accept-action--hidden");
        }
    }

    /**
     * Update the "Will Keep" and "Will Remove Mapping" labels on cards
     */
    _updateCardLabels(keepSubscriptionId, removeSubscriptionIds) {
        if (!this.hasCardTarget) return;

        this.cardTargets.forEach(card => {
            const cardSubscriptionId = parseInt(card.dataset.subscriptionId, 10);
            const keepLabel = card.querySelector('[data-preview-label="keep"]');
            const removeLabel = card.querySelector('[data-preview-label="remove"]');

            if (cardSubscriptionId === parseInt(keepSubscriptionId, 10)) {
                // This is the subscription to keep
                if (keepLabel) {
                    keepLabel.classList.remove("duplicate-mappings__preview-label--hidden");
                }
                if (removeLabel) {
                    removeLabel.classList.add("duplicate-mappings__preview-label--hidden");
                }
                card.classList.add("duplicate-mappings__subscription-card--preview-keep");
                card.classList.remove("duplicate-mappings__subscription-card--preview-remove");
            } else if (removeSubscriptionIds.map(id => parseInt(id, 10)).includes(cardSubscriptionId)) {
                // This is a subscription to remove mapping from
                if (keepLabel) {
                    keepLabel.classList.add("duplicate-mappings__preview-label--hidden");
                }
                if (removeLabel) {
                    removeLabel.classList.remove("duplicate-mappings__preview-label--hidden");
                }
                card.classList.remove("duplicate-mappings__subscription-card--preview-keep");
                card.classList.add("duplicate-mappings__subscription-card--preview-remove");
            } else {
                // Reset this card
                if (keepLabel) {
                    keepLabel.classList.add("duplicate-mappings__preview-label--hidden");
                }
                if (removeLabel) {
                    removeLabel.classList.add("duplicate-mappings__preview-label--hidden");
                }
                card.classList.remove("duplicate-mappings__subscription-card--preview-keep");
                card.classList.remove("duplicate-mappings__subscription-card--preview-remove");
            }
        });
    }

    /**
     * Reset the preview state (hide badges and labels)
     */
    _resetPreview() {
        // Hide the preview badges
        if (this.hasResolutionPreviewTarget) {
            this.resolutionPreviewTarget.classList.add("duplicate-mappings__resolution-preview--hidden");
        }

        // Show the reason display again
        if (this.hasReasonDisplayTarget) {
            this.reasonDisplayTarget.classList.remove("duplicate-mappings__reason--hidden");
        }

        // Reset all card labels
        if (this.hasCardTarget) {
            this.cardTargets.forEach(card => {
                const keepLabel = card.querySelector('[data-preview-label="keep"]');
                const removeLabel = card.querySelector('[data-preview-label="remove"]');

                if (keepLabel) {
                    keepLabel.classList.add("duplicate-mappings__preview-label--hidden");
                }
                if (removeLabel) {
                    removeLabel.classList.add("duplicate-mappings__preview-label--hidden");
                }
                card.classList.remove("duplicate-mappings__subscription-card--preview-keep");
                card.classList.remove("duplicate-mappings__subscription-card--preview-remove");
            });
        }

        // Hide the Accept button
        if (this.hasAcceptContainerTarget) {
            this.acceptContainerTarget.classList.add("duplicate-mappings__accept-action--hidden");
        }

        // Hide any error messages
        this._hideError();
    }

    _updateSelectedCard() {
        if (!this.hasCardTarget || !this.hasRadioTarget) {
            return;
        }

        // Find which radio is selected
        const selectedRadio = this.radioTargets.find(radio => radio.checked);
        if (!selectedRadio) return;

        const selectedSubscriptionId = selectedRadio.value;

        // Update card styling
        this.cardTargets.forEach(card => {
            const cardSubscriptionId = card.dataset.subscriptionId;
            if (cardSubscriptionId === selectedSubscriptionId) {
                card.classList.add("duplicate-mappings__subscription-card--selected");
            } else {
                card.classList.remove("duplicate-mappings__subscription-card--selected");
            }
        });
    }

    _updateButtonState() {
        if (!this.hasDryRunCheckboxTarget || !this.hasSubmitButtonTextTarget) {
            return;
        }

        const isDryRun = this.dryRunCheckboxTarget.checked;

        if (isDryRun) {
            this.submitButtonTextTarget.textContent = "Preview Resolution";
            if (this.hasSubmitButtonTarget) {
                this.submitButtonTarget.classList.remove("stop");
            }
        } else {
            this.submitButtonTextTarget.textContent = "Execute Resolution";
            if (this.hasSubmitButtonTarget) {
                this.submitButtonTarget.classList.add("stop");
            }
        }
    }

    _setLoading(isLoading) {
        if (!this.hasSubmitButtonTarget) return;

        if (isLoading) {
            this.submitButtonTarget.disabled = true;
            if (this.hasSubmitButtonTextTarget) {
                this._originalButtonText = this.submitButtonTextTarget.textContent;
                this.submitButtonTextTarget.textContent = "Loading...";
            }
        } else {
            this.submitButtonTarget.disabled = false;
            if (this.hasSubmitButtonTextTarget && this._originalButtonText) {
                this.submitButtonTextTarget.textContent = this._originalButtonText;
            }
        }
    }

    _setAcceptLoading(isLoading) {
        if (!this.hasAcceptButtonTarget) return;

        if (isLoading) {
            this.acceptButtonTarget.disabled = true;
            if (this.hasAcceptButtonTextTarget) {
                this._originalAcceptButtonText = this.acceptButtonTextTarget.textContent;
                this.acceptButtonTextTarget.textContent = "Executing...";
            }
        } else {
            this.acceptButtonTarget.disabled = false;
            if (this.hasAcceptButtonTextTarget && this._originalAcceptButtonText) {
                this.acceptButtonTextTarget.textContent = this._originalAcceptButtonText;
            }
        }
    }

    _showError(message) {
        if (!this.hasErrorMessageTarget) return;

        this.errorMessageTarget.textContent = message;
        this.errorMessageTarget.classList.remove("duplicate-mappings__error-message--hidden");
    }

    _hideError() {
        if (!this.hasErrorMessageTarget) return;

        this.errorMessageTarget.classList.add("duplicate-mappings__error-message--hidden");
        this.errorMessageTarget.textContent = "";
    }

    _getCSRFToken() {
        const metaTag = document.querySelector('meta[name="csrf-token"]');
        return metaTag ? metaTag.content : "";
    }
}
